import { globalStores } from '.';
import { IPoint2d } from '../math/IPoint2d';
import { Hole, HoleType } from '../entities/hole';
import { Point2dUtils } from '../math/Point2dUtils';
import { Vector2, Vector3 } from 'three';
import { getUniqueId } from '../utils';
import { EFaceType } from '../entities/type';
import { IWindowData } from '../data/model/model';
import { LineSeg2d } from '../math/LineSeg2d';
import { EEntityType } from '../types';
import { RelativeWallInfo } from '../data/wall/Wall';
import { thresholdValue } from '../math/const';

export function getRelateHoles(
  entityId: string,
  start: IPoint2d,
  end: IPoint2d,
  thickness: number,
  planeHeight: number
) {
  const holes: Hole[] = [];
  if (start.x === 0 && start.y === 0 && end.x === 0 && end.y === 0) return holes;

  const windowDatas = globalStores.modelStore.getWindowsByWallId(entityId);
  for (const windowData of windowDatas) {
    const { width, height, offGround, distance } = windowData;
    const direction = Point2dUtils.getDirection(start, end);
    const centerPoint = Point2dUtils.addDistance(start, distance, direction);

    holes.push(
      new Hole({
        dwId: windowData.id,
        type: HoleType.Rect,
        info: { center: centerPoint, width, height, offGround },
        wallStart: start,
        wallEnd: end,
        thickness,
        wallHeight: planeHeight
      })
    );
  }
  return holes;
}

export function getHoleOutlines(holes: Hole[], planeStart: IPoint2d, planeEnd: IPoint2d) {
  const holeOutlines: IPoint2d[][] = [];
  for (const hole of holes) {
    const holePoints: IPoint2d[] = [];
    const holePath = hole.getHolePath(planeStart, planeEnd);
    if (holePath) {
      const points = holePath.getPoints();
      points.forEach((point) => holePoints.push({ x: point.x, y: point.y }));
      holeOutlines.push(holePoints);
    }
  }

  return holeOutlines;
}

export function getDoorWindowRelateInfo(doorWindowData: IWindowData) {
  let relateSeg: LineSeg2d | undefined;
  let thickness = 0;
  if (doorWindowData.relativeWallInfo.wallType === EEntityType.SeparateWall) {
    const relateSeparateWallData = globalStores.separateWallStore.getSepData(
      doorWindowData.relativeWallInfo.relativeWallId,
      false
    );
    if (relateSeparateWallData) {
      relateSeg = new LineSeg2d(relateSeparateWallData.start, relateSeparateWallData.end);
      thickness = relateSeparateWallData.thickness;
    }
  } else {
    const relateWallData = globalStores.wallStore.getWallData(doorWindowData.relativeWallInfo.relativeWallId);
    if (relateWallData) {
      relateSeg = new LineSeg2d(relateWallData.baseData.start, relateWallData.baseData.end);
      thickness = relateWallData.baseData.thickness;
    }
  }

  return { relateSeg, thickness };
}

export function getHoleFaceDatas(doorWindowData: IWindowData) {
  const defaultContourMaterialInfo = globalStores.getDefaultMaterial();
  const { relateSeg, thickness } = getDoorWindowRelateInfo(doorWindowData);

  if (!relateSeg) {
    return [];
  }
  const rotateZ = new Vector2(relateSeg.end.x - relateSeg.start.x, relateSeg.end.y - relateSeg.start.y).angle();

  const { width: holeWidth, offGround, height: holeHeight, center, id: dwId } = doorWindowData;
  const topHoleData = {
    id: getUniqueId(),
    holes: [],
    materialInfo: defaultContourMaterialInfo,
    type: EFaceType.HoleFace,
    baseData: {
      relativeEntityId: doorWindowData.relativeWallInfo.relativeWallId,
      dwId,
      position: new Vector3(center.x, center.y, offGround + holeHeight),
      width: holeWidth,
      thickness,
      rotation: new Vector3(Math.PI, 0, rotateZ)
    }
  };

  const bottomHoleData = {
    id: getUniqueId(),
    holes: [],
    materialInfo: defaultContourMaterialInfo,
    type: EFaceType.HoleFace,
    baseData: {
      relativeEntityId: doorWindowData.relativeWallInfo.relativeWallId,
      dwId,
      position: new Vector3(center.x, center.y, offGround),
      width: holeWidth,
      thickness,
      rotation: new Vector3(0, 0, rotateZ)
    }
  };

  const leftCenter = {
    x: center.x - (holeWidth / 2) * Math.cos(rotateZ),
    y: center.y - (holeWidth / 2) * Math.sin(rotateZ)
  };
  const leftHoleData = {
    id: getUniqueId(),
    holes: [],
    materialInfo: defaultContourMaterialInfo,
    type: EFaceType.HoleFace,
    baseData: {
      relativeEntityId: doorWindowData.relativeWallInfo.relativeWallId,
      dwId,
      position: new Vector3(leftCenter.x, leftCenter.y, offGround + holeHeight / 2),
      width: holeHeight,
      thickness,
      rotation: new Vector3(0, Math.PI / 2, rotateZ)
    }
  };

  const rightCenter = {
    x: center.x + (holeWidth / 2) * Math.cos(rotateZ),
    y: center.y + (holeWidth / 2) * Math.sin(rotateZ)
  };
  const rightHoleData = {
    id: getUniqueId(),
    holes: [],
    materialInfo: defaultContourMaterialInfo,
    type: EFaceType.HoleFace,
    baseData: {
      relativeEntityId: doorWindowData.relativeWallInfo.relativeWallId,
      dwId,
      position: new Vector3(rightCenter.x, rightCenter.y, offGround + holeHeight / 2),
      width: holeHeight,
      thickness,
      rotation: new Vector3(0, -Math.PI / 2, rotateZ)
    }
  };

  return [topHoleData, bottomHoleData, leftHoleData, rightHoleData];
}

export function getRelativeWallIdsThatNeedUpdateShape(wallId: string, relativeWallInfos?: RelativeWallInfo) {
  // 更新旧的关联墙体轮廓
  const updateShapeSet = new Set<string>();
  updateShapeSet.add(wallId);
  relativeWallInfos?.mainStartConnectToPoint.forEach((item) => {
    updateShapeSet.add(item.id);
  });
  relativeWallInfos?.mainEndConnectToPoint.forEach((item) => {
    updateShapeSet.add(item.id);
  });
  // relativeWallInfos?.mainStartOnWall.forEach((item) => {
  //   updateShapeSet.add(item.id);
  // });
  // relativeWallInfos?.mainEndOnWall.forEach((item) => {
  //   updateShapeSet.add(item.id);
  // });
  relativeWallInfos?.startPointOnMainWallInternal.forEach((item) => {
    updateShapeSet.add(item.id);
  });
  relativeWallInfos?.endPointOnMainWallInternal.forEach((item) => {
    updateShapeSet.add(item.id);
  });
  // 端点埋在本墙实心带内的后建墙，其端盖是按本墙轮廓裁出来的，本墙一动就得跟着重算
  relativeWallInfos?.startPointInMainWallBody?.forEach((item) => {
    updateShapeSet.add(item.id);
  });
  relativeWallInfos?.endPointInMainWallBody?.forEach((item) => {
    updateShapeSet.add(item.id);
  });

  return updateShapeSet;
}

// 从轮廓线交点获取墙中线分段点
export function getWallSplitPoints(wallId: string, wallType: EEntityType.Wall | EEntityType.SeparateWall): IPoint2d[] {
  const splitPoints: IPoint2d[] = [];

  // ================= separate wall =================
  if (wallType === EEntityType.SeparateWall) {
    const relativeSepWallData =
      globalStores.separateWallStore.getSepData(wallId, false) ||
      globalStores.separateWallStore.getSepData(wallId, true);

    if (!relativeSepWallData) return splitPoints;

    if (relativeSepWallData.center) return [];

    const wallStart = relativeSepWallData.start;
    const wallEnd = relativeSepWallData.end;
    const wallSeg = new LineSeg2d(wallStart, wallEnd);

    globalStores.edgeStore.getSepEdges(relativeSepWallData.relateRoomId).forEach((edge) => {
      if (edge.sepId === relativeSepWallData.id) {
        addToSortedPointArray(wallSeg, splitPoints, edge.start);
        addToSortedPointArray(wallSeg, splitPoints, edge.end);
      }
    });
    return splitPoints;
  }

  // ================= wall =================
  const relativeWallData = globalStores.wallStore.getWallData(wallId);
  if (!relativeWallData) return splitPoints;

  const { start, end, center } = relativeWallData.baseData;

  // 弧形墙不提供分段信息
  if (center) return [];

  const wallSeg = new LineSeg2d(start, end);

  // 获取墙中线的分段点
  globalStores.connectedWallOutlines.forEach((outline) => {
    outline.edges.forEach((edge) => {
      if (edge.wallId === relativeWallData.id) {
        addToSortedPointArray(wallSeg, splitPoints, edge.start);
        addToSortedPointArray(wallSeg, splitPoints, edge.end);
      }
    });
  });

  return splitPoints;
}
// export function getWallSplitPointRatios(
//   wallId: string,
//   wallType: EEntityType.Wall | EEntityType.SeparateWall
// ): number[] {
// }
function addToSortedPointArray(referenceSeg: LineSeg2d, array: IPoint2d[], point: IPoint2d) {
  const footPoint = referenceSeg.getFootOfPerpendicular(point, false);
  if (array.length === 0) {
    array.push(footPoint);
    return;
  }

  for (let i = array.length - 1; i >= 0; i--) {
    const vec = Point2dUtils.subtracted(footPoint, array[i]);
    if (Math.hypot(vec.x, vec.y) < thresholdValue) return; // 共点跳过
    const projectDistance = Point2dUtils.projectScalar(vec, referenceSeg.getDirectionVector());
    if (projectDistance > 0) {
      array.splice(i + 1, 0, footPoint);
      return;
    }
  }
  array.unshift(footPoint);
}
// function addToSortedRatioArray(referenceSeg: IShapeSeg2d, array: number[], point: IPoint2d) {
//   // const footPoint = referenceSeg.getFootOfPerpendicular(point, false);
//   const footRatio = getFootRatio(referenceSeg, point);

//   if (array.length === 0) {
//     array.push(footRatio);
//     return;
//   }

//   for (let i = array.length - 1; i >= 0; i--) {
//     if (Math.abs(footRatio - array[i]) < thresholdValue) return; // 共点跳过

//     if (footRatio - array[i] < 0) {
//       array.splice(i + 1, 0, footRatio);
//       return;
//     }
//   }
//   array.unshift(footRatio);
// }
